import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type Doctor = {
  id: string;
  slug: string;
  name: string;
  shortTitle?: string | null;
  portraitUrl?: string | null;
  published?: boolean;
  updatedAt?: string;
};

type MediaOpt = { id?: string; name: string; url: string };

const DoctorsYonetim: React.FC = () => {

  const [items, setItems] = React.useState<Doctor[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');

  // create form
  const [newSlug, setNewSlug] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [newShort, setNewShort] = React.useState('');
  const [newPortrait, setNewPortrait] = React.useState('');
  const [newPublished, setNewPublished] = React.useState(true);
  const [creating, setCreating] = React.useState(false);

  // media options for portrait selection
  const [mediaOpts, setMediaOpts] = React.useState<MediaOpt[]>([]);

  // edit
  const [editingId, setEditingId] = React.useState('');
  const [editSlug, setEditSlug] = React.useState('');
  const [editName, setEditName] = React.useState('');
  const [editShort, setEditShort] = React.useState('');
  const [editPortrait, setEditPortrait] = React.useState('');
  const [editPublished, setEditPublished] = React.useState<boolean>(true);
  const [saving, setSaving] = React.useState(false);

  // delete
  const [confirmId, setConfirmId] = React.useState('');
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState('');

  const fetchList = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      let res = await fetch(toUrl('/api/doctors/admin'));
      if (!res.ok) res = await fetch(toUrl('/api/doctors?limit=100&offset=0'));
      if (!res.ok) throw new Error('Liste alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally { setLoading(false); }
  }, []);

  const fetchMediaOpts = React.useCallback(async () => {
    try {
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      const opts: MediaOpt[] = (data || []).map((m: any) => ({ id: m.id, name: m.name || m.filename || m.url, url: m.url }));
      setMediaOpts(opts);
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { fetchList(); fetchMediaOpts(); }, [fetchList, fetchMediaOpts]);

  const filtered = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(d =>
      (d.name || '').toLowerCase().includes(term) ||
      (d.slug || '').toLowerCase().includes(term) ||
      (d.shortTitle || '').toLowerCase().includes(term)
    );
  }, [items, q]);

  const resetCreate = () => { setNewSlug(''); setNewName(''); setNewShort(''); setNewPortrait(''); setNewPublished(true); };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSlug.trim() || !newName.trim()) { setError('Lütfen slug ve ad girin'); return; }
    try {
      setCreating(true); setError('');
      const res = await fetch(toUrl('/api/doctors/admin'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: newSlug.trim(), name: newName.trim(), shortTitle: newShort.trim() || null, portraitUrl: newPortrait.trim() || null, published: !!newPublished })
      });
      if (!res.ok) throw new Error('Oluşturma başarısız');
      resetCreate(); await fetchList();
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreating(false); }
  };

  const openEdit = (d: Doctor) => {
    setEditingId(d.id);
    setEditSlug(d.slug);
    setEditName(d.name);
    setEditShort(d.shortTitle || '');
    setEditPortrait(d.portraitUrl || '');
    setEditPublished(d.published ?? true);
  };
  const cancelEdit = () => { setEditingId(''); setEditSlug(''); setEditName(''); setEditShort(''); setEditPortrait(''); setEditPublished(true); };

  const onSave = async (id: string) => {
    if (!editSlug.trim() || !editName.trim()) { setError('Lütfen slug ve ad girin'); return; }
    try {
      setSaving(true); setError('');
      const res = await fetch(toUrl(`/api/doctors/admin/${id}`), {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: editSlug.trim(), name: editName.trim(), shortTitle: editShort.trim() || null, portraitUrl: editPortrait.trim() || null, published: !!editPublished })
      });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEdit(); await fetchList();
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSaving(false); }
  };

  const onDelete = (id: string) => setConfirmId(id);
  const confirmDelete = async () => {
    if (!confirmId) return;
    try {
      setConfirmLoading(true); setDeletingId(confirmId);
      const res = await fetch(toUrl(`/api/doctors/admin/${confirmId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Silme başarısız');
      setConfirmId(''); await fetchList();
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setConfirmLoading(false); setDeletingId(''); }
  };

  return (
    <div className="admin-page" style={{ padding: 12, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Doktorlar</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Toplam: {items.length}</div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ margin: 0, marginBottom: 12 }}>Yeni Doktor Ekle</h3>
        <form onSubmit={onCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Slug</label>
            <input type="text" value={newSlug} onChange={(e) => setNewSlug(e.target.value)} className="input" placeholder="ummuhan-ariguloglu" required />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Ad</label>
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} className="input" placeholder="Opr. Dr. Ümmühan" required />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Kısa Ünvan (ops.)</label>
            <input type="text" value={newShort} onChange={(e) => setNewShort(e.target.value)} className="input" placeholder="Kadın Hast. ve Doğum Uzmanı" />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Portre (Medya - ops.)</label>
            <select value={newPortrait} onChange={(e) => setNewPortrait(e.target.value)} className="input">
              <option value="">Seçiniz</option>
              {mediaOpts.map((m) => (
                <option key={m.url} value={m.url}>{m.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Yayınla</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input id="newPublished" type="checkbox" checked={newPublished} onChange={(e) => setNewPublished(e.target.checked)} />
              <label htmlFor="newPublished">Published</label>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={creating} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creating ? 0.8 : 1 }}>{creating ? 'Ekleniyor...' : 'Ekle'}</button>
          </div>
        </form>
        {newPortrait && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Önizleme:</div>
            <div style={{ width: 96, height: 96, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <img
                src={toUrl(newPortrait)}
                alt="portrait"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                crossOrigin="anonymous"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  const current = el.getAttribute('src') || '';
                  const raw = newPortrait;
                  const pref = toUrl(newPortrait);
                  const next = current === pref ? raw : pref;
                  if (current !== next) el.src = next;
                }}
              />
            </div>
          </div>
        )}
        {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
      </div>

      <div className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: ad, slug, ünvan" className="input" style={{ width: 280 }} />
            {q && <button onClick={() => setQ('')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Temizle</button>}
          </div>
          <button onClick={fetchList} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
        </div>
        <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 1 }}>Portre</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 1 }}>Slug</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 1 }}>Ad</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 1 }}>Ünvan</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 1 }}>Published</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 1 }}>Güncellenme</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 1 }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
              {!loading && filtered.length === 0 && (<tr><td colSpan={7} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
              {filtered.map(d => (
                <tr key={d.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === d.id ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select value={editPortrait} onChange={(e) => setEditPortrait(e.target.value)} className="input">
                          <option value="">Portre: Seçiniz</option>
                          {mediaOpts.map((m) => (
                            <option key={m.url} value={m.url}>{m.name}</option>
                          ))}
                        </select>
                        {editPortrait && (
                          <div style={{ width: 64, height: 64, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            <img
                              src={toUrl(editPortrait)}
                              alt="portrait"
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              crossOrigin="anonymous"
                              onError={(e) => {
                                const el = e.currentTarget as HTMLImageElement;
                                const current = el.getAttribute('src') || '';
                                const raw = editPortrait;
                                const pref = toUrl(editPortrait);
                                const next = current === pref ? raw : pref;
                                if (current !== next) el.src = next;
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ) : d.portraitUrl ? (
                      <div style={{ width: 96, height: 96, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        <img
                          src={toUrl(d.portraitUrl)}
                          alt={d.name}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          crossOrigin="anonymous"
                          onError={(e) => {
                            const el = e.currentTarget as HTMLImageElement;
                            const current = el.getAttribute('src') || '';
                            const raw = d.portraitUrl as string;
                            const pref = toUrl(d.portraitUrl as string);
                            const next = current === pref ? raw : pref;
                            if (current !== next) el.src = next;
                          }}
                        />
                      </div>
                    ) : <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === d.id ? (
                      <input type="text" value={editSlug} onChange={(e) => setEditSlug(e.target.value)} className="input" />
                    ) : d.slug}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === d.id ? (
                      <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="input" />
                    ) : d.name}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === d.id ? (
                      <input type="text" value={editShort} onChange={(e) => setEditShort(e.target.value)} className="input" />
                    ) : (d.shortTitle || '-')}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === d.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input id={`pub-${d.id}`} type="checkbox" checked={editPublished} onChange={(e) => setEditPublished(e.target.checked)} />
                        <label htmlFor={`pub-${d.id}`}>Published</label>
                      </div>
                    ) : (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: d.published ? '#ecfdf5' : '#fef2f2', color: d.published ? '#065f46' : '#991b1b', border: `1px solid ${d.published ? '#a7f3d0' : '#fecaca'}` }}>{d.published ? 'Aktif' : 'Pasif'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>{d.updatedAt ? new Date(d.updatedAt).toLocaleString() : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {editingId === d.id ? (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => onSave(d.id)} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: saving ? 0.8 : 1 }}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        <button onClick={cancelEdit} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => openEdit(d)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                        <button onClick={() => onDelete(d.id)} disabled={deletingId === d.id} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: deletingId === d.id ? 0.8 : 1 }}>Sil</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {confirmId && (
        <div role="dialog" aria-modal="true" aria-labelledby="docDelTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setConfirmId(''); }} onKeyDown={(e) => { if (e.key === 'Escape') setConfirmId(''); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(420px,92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="docDelTitle" style={{ margin: 0, fontSize: 18 }}>Silme Onayı</h5>
              <button type="button" aria-label="Close" onClick={() => setConfirmId('')} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>Bu doktoru silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmId('')} disabled={confirmLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
              <button type="button" onClick={confirmDelete} disabled={confirmLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: confirmLoading ? 0.8 : 1 }}>{confirmLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorsYonetim;
